import React, { FC, useMemo } from "react";
import { Box, Typography } from "@mui/material";

interface BoxPlotData {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

interface BoxPlotSeries {
  data: BoxPlotData;
  label: string;
  color: string;
}

interface BoxPlotProps {
  series: BoxPlotSeries[];
  title?: string;
  currencyCode?: string;
}

const formatAxisValue = (value: number, currencyCode: string): string => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return value.toLocaleString("en-US");
  }
};

const TICK_COUNT = 8;
const H_PADDING = 12;
const TOP_PADDING = 4;
const ROW_HEIGHT = 44;
const BOX_HALF = 12;
const AXIS_HEIGHT = 48;
const LEGEND_HEIGHT = 20;

const BoxPlot: FC<BoxPlotProps> = ({ series, title, currencyCode = "EUR" }) => {
  const width = 280;

  const { scaleMin, range } = useMemo(() => {
    const allValues = series.flatMap((s) => Object.values(s.data));
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);


return { scaleMin: min, scaleMax: max, range: max - min };
  }, [series]);

  const plotWidth = width - 2 * H_PADDING;
  const plotAreaHeight = series.length * ROW_HEIGHT;
  const plotBottom = TOP_PADDING + plotAreaHeight;
  const totalHeight = TOP_PADDING + plotAreaHeight + AXIS_HEIGHT + LEGEND_HEIGHT;

  const normalize = (value: number): number =>
    range === 0 ? 0.5 : (value - scaleMin) / range;

  const xPos = (value: number): number =>
    H_PADDING + normalize(value) * plotWidth;

  const ticks = useMemo(() => {
    const result: number[] = [];

    for (let i = 0; i < TICK_COUNT; i++) {
      result.push(scaleMin + (range * i) / (TICK_COUNT - 1));
    }

return result;
  }, [scaleMin, range]);

  const legendY = plotBottom + AXIS_HEIGHT + 2;

  return (
    <Box>
      {title && (
        <Typography
          variant="body2"
          textAlign="center"
          fontWeight="bold"
          sx={{ mb: 0.5, lineHeight: 1.4 }}
        >
          {title}
        </Typography>
      )}
      <Box display="flex" justifyContent="center" px={4}>
        <svg width={width} height={totalHeight} style={{ overflow: "visible" }}>
          {/* Grid lines */}
          {ticks.map((tick, i) => (
            <line
              key={`grid-${i}`}
              x1={xPos(tick)}
              y1={TOP_PADDING}
              x2={xPos(tick)}
              y2={plotBottom}
              stroke="#e0e0e0"
              strokeWidth={1}
              strokeDasharray="3,3"
            />
          ))}

          {/* Series rows */}
          {series.map((s, i) => {
            const cy = TOP_PADDING + ROW_HEIGHT * i + ROW_HEIGHT / 2;


return (
              <g key={i}>
                {/* Whisker line */}
                <line
                  x1={xPos(s.data.min)}
                  y1={cy}
                  x2={xPos(s.data.max)}
                  y2={cy}
                  stroke={s.color}
                  strokeWidth={2}
                />

                {/* Min cap */}
                <line
                  x1={xPos(s.data.min)}
                  y1={cy - 6}
                  x2={xPos(s.data.min)}
                  y2={cy + 6}
                  stroke={s.color}
                  strokeWidth={2}
                />

                {/* Max cap */}
                <line
                  x1={xPos(s.data.max)}
                  y1={cy - 6}
                  x2={xPos(s.data.max)}
                  y2={cy + 6}
                  stroke={s.color}
                  strokeWidth={2}
                />

                {/* IQR box */}
                <rect
                  x={xPos(s.data.q1)}
                  y={cy - BOX_HALF}
                  width={Math.max(0, xPos(s.data.q3) - xPos(s.data.q1))}
                  height={BOX_HALF * 2}
                  fill={s.color}
                  fillOpacity={0.15}
                  stroke={s.color}
                  strokeWidth={2}
                />

                {/* Median line */}
                <line
                  x1={xPos(s.data.median)}
                  y1={cy - BOX_HALF}
                  x2={xPos(s.data.median)}
                  y2={cy + BOX_HALF}
                  stroke={s.color}
                  strokeWidth={3}
                />
              </g>
            );
          })}

          {/* Shared x-axis */}
          <line
            x1={H_PADDING}
            y1={plotBottom}
            x2={width - H_PADDING}
            y2={plotBottom}
            stroke="#999"
            strokeWidth={1}
          />
          {ticks.map((tick, i) => {
            const tx = xPos(tick);
            const ty = plotBottom + 8;


return (
              <g key={`tick-${i}`}>
                <line
                  x1={tx}
                  y1={plotBottom}
                  x2={tx}
                  y2={plotBottom + 4}
                  stroke="#999"
                  strokeWidth={1}
                />
                <text
                  x={0}
                  y={0}
                  transform={`translate(${tx},${ty}) rotate(-45)`}
                  textAnchor="end"
                  fontSize="8"
                  fill="#666"
                >
                  {formatAxisValue(Math.round(tick), currencyCode)}
                </text>
              </g>
            );
          })}

          {/* Legend (below axis) */}
          <g>
            {series.map((s, i) => {
              const legendItemWidth = width / series.length;
              const cx = legendItemWidth * i + legendItemWidth / 2;


return (
                <g key={`legend-${i}`}>
                  <rect
                    x={cx - 26}
                    y={legendY}
                    width={10}
                    height={10}
                    rx={2}
                    fill={s.color}
                    opacity={0.85}
                  />
                  <text
                    x={cx - 12}
                    y={legendY + 9}
                    fontSize="10"
                    fill="#333"
                    fontWeight="500"
                  >
                    {s.label}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </Box>
    </Box>
  );
};

export default BoxPlot;
